// ChatSessionContext.jsx
// Responsibility: Managing the state of the active chat session
import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import * as apiChat from '../api/chat';
import { POLLING_INTERVAL_MS, MAX_POLLING_ATTEMPTS, DEMO_PROMPT_LIMIT } from '../config';
import { useAuth } from './AuthContext';
import { useConversations } from './ConversationsContext';

const ChatSessionContext = createContext();

export const ChatSessionProvider = ({ children }) => {
    // --- Core Chat State ---
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
    const [isRouting, setIsRouting] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conversationId, setConversationId] = useState(null);
    const [selectedModel, setSelectedModel] = useState("Route AI");
    const [streamingContent, setStreamingContent] = useState('');
    const [streamingMessageMeta, setStreamingMessageMeta] = useState(null);
    const [viewMode, setViewMode] = useState('chat');

    // Get contexts
    const { userId, userName, isSessionValid, setAuthError, setIsSessionValid, refreshUserProfile } = useAuth();
    const { conversations, saveConversation, loadConversation: loadConversationData } = useConversations();

    // Refs for WebSocket management
    const socketRef = useRef(null);
    const routingSocketRef = useRef(null); // stores the cancel() function returned by createRoutingSocket
    const isFinalizingRef = useRef(false);
    const streamingContentRef = useRef('');

    const isNewConversationRef = useRef(false);
    const needsTitleFetchRef = useRef(null);

    // --- WebSocket Management ---
    const closeWebSocket = useCallback(() => {
        if (socketRef.current) {
            const socket = socketRef.current;

            if (socket.readyState === WebSocket.CONNECTING ||
                socket.readyState === WebSocket.OPEN) {
                console.log(`ChatSessionContext: Closing WebSocket for user ${userId}`);
                socket.close();
            }

            socketRef.current = null;
        }
    }, [userId]);

    const closeRoutingSocket = useCallback(() => {
        if (routingSocketRef.current) {
            routingSocketRef.current(); // calls cancel() — silently closes socket without triggering callbacks
            routingSocketRef.current = null;
        }
    }, []);

    // Cleanup effect for WebSocket
    useEffect(() => {
        return () => {
            console.log('ChatSessionContext: Cleaning up WebSocket and streaming state');
            closeWebSocket();
            closeRoutingSocket();
            isFinalizingRef.current = false;
            streamingContentRef.current = '';
            setStreamingContent('');
            setStreamingMessageMeta(null);
        };
    }, [closeWebSocket, closeRoutingSocket]);

    useEffect(() => {
        // Watch for when streaming completes for a new conversation
        if (!isWaitingForResponse && 
            !streamingContent && 
            needsTitleFetchRef.current) {
            
            const convId = needsTitleFetchRef.current;
            console.log(`ChatSessionContext: Streaming completed, fetching title for ${convId}`);
            
            // Reset the ref
            needsTitleFetchRef.current = null;
            
            // Fetch the backend-generated title
            const fetchBackendTitle = async () => {
                try {
                    await new Promise(resolve => setTimeout(resolve, 2000)); // Wait for backend to process
                    
                    const freshConversations = await apiChat.fetchConversations();
                    const updatedConv = freshConversations.find(c => c.conversation_id === convId);
                    
                    if (updatedConv && updatedConv.title && updatedConv.title !== 'Untitled Chat') {
                        console.log(`ChatSessionContext: Updating title to: ${updatedConv.title}`);
                        
                        saveConversation({
                            id: convId,
                            title: updatedConv.title,
                            lastUpdated: updatedConv.last_updated_at || new Date().toISOString(),
                            userId: userId
                        });
                    }
                } catch (error) {
                    console.error('ChatSessionContext: Failed to fetch backend title:', error);
                }
            };
            
            fetchBackendTitle();
        }
    }, [isWaitingForResponse, streamingContent, userId, saveConversation]);

    // --- Save Current Chat ---
    const saveCurrentChat = useCallback(() => {
        if (conversationId && messages.some(m => m.role === 'user')) {
            // Find the existing conversation to check if it exists
            const existingConversation = conversations.find(c => c.id === conversationId);
            
            if (existingConversation) {
                // For existing conversations, only update lastUpdated, preserve the title
                const conversationToSave = {
                    id: conversationId,
                    title: existingConversation.title, // Preserve existing title
                    lastUpdated: new Date().toISOString(),
                    userId: userId
                };
                
                saveConversation(conversationToSave);
                console.log(`ChatSessionContext: Saved current chat metadata for conversation ${conversationId}`);
            } else {
                // For new conversations (shouldn't happen here, but just in case)
                const firstUserMessage = messages.find(m => m.role === 'user');
                const title = firstUserMessage 
                    ? firstUserMessage.content.substring(0, 100) 
                    : 'Untitled Chat';
                    
                const conversationToSave = {
                    id: conversationId,
                    title: title,
                    lastUpdated: new Date().toISOString(),
                    userId: userId
                };
                
                saveConversation(conversationToSave);
            }
        }
    }, [conversationId, messages, conversations, userId, saveConversation]);

    // --- Stream Final Answer ---
    const streamFinalAnswer = async (prompt, chosenModel, convId, uploadedFiles = [], webSearchEnabled = false, routingDetails = null) => {
        closeWebSocket();
        isFinalizingRef.current = false;
        streamingContentRef.current = '';

        // Track if this is a new conversation
        const isNewConv = !conversations.find(c => c.id === convId);
        isNewConversationRef.current = isNewConv;

        // Mark that we need to fetch title when streaming completes
        if (isNewConv) {
            needsTitleFetchRef.current = convId;
            console.log(`ChatSessionContext: Marked conversation ${convId} for title fetch`);
        }

        setStreamingContent('');
        setStreamingMessageMeta({
            role: 'assistant',
            modelUsed: chosenModel,
            isStreaming: true
        });

        const callbacks = {
            onMessage: (data) => {
                if (data.content) {
                    const newChunk = data.content;
                    streamingContentRef.current += newChunk;
                    setStreamingContent(prev => prev + newChunk);
                }
            },
            onClose: () => {
                if (isFinalizingRef.current) return;
                isFinalizingRef.current = true;

                const finalContent = streamingContentRef.current;
                
                if (!finalContent.trim()) {
                    console.log('ChatSessionContext: Empty streaming content, cleaning up');
                    setIsWaitingForResponse(false);
                    setStreamingContent('');
                    setStreamingMessageMeta(null);
                    return;
                }

                const finalMessage = {
                    role: 'assistant',
                    content: finalContent,
                    modelUsed: chosenModel,
                    isStreaming: false,
                    webSearchEnabled: webSearchEnabled,
                    routingDetails: routingDetails,
                };

                setMessages(prev => [...prev, finalMessage]);

                // Save conversation metadata after successful response
                // Must be outside setMessages updater to avoid setState-during-render warning
                if (convId) {
                    if (isNewConversationRef.current) {
                        const title = prompt.substring(0, 100) || 'Untitled Chat';
                        saveConversation({
                            id: convId,
                            title: title,
                            lastUpdated: new Date().toISOString(),
                            userId: userId
                        });
                    } else {
                        // For existing conversations, only update timestamp
                        // Let saveConversation in ConversationsContext preserve the existing title
                        saveConversation({
                            id: convId,
                            lastUpdated: new Date().toISOString(),
                            userId: userId
                        });
                    }
                }

                setStreamingContent('');
                setStreamingMessageMeta(null);
                setIsWaitingForResponse(false);
                isNewConversationRef.current = false;
                setIsSubmitting(false);
                console.log('ChatSessionContext: Streaming completed successfully');

                refreshUserProfile().catch(err => {
                    console.error('Failed to refresh profile after message:', err);
                });

            },
            onError: (error) => {
                console.error(`ChatSessionContext: WebSocket error for user ${userId}:`, error);
                
                // Handle authentication errors
                if (error.message?.includes('Authentication') || 
                    error.message?.includes('401')) {
                    setAuthError('Authentication failed. Please sign in again.');
                    setIsSessionValid(false);
                }
                
                if (isFinalizingRef.current) return;
                isFinalizingRef.current = true;

                const partialContent = streamingContentRef.current;
                if (partialContent) {
                    setMessages(prev => [...prev, {
                        role: 'assistant',
                        content: partialContent + '\n\n*[Error: Connection interrupted]*',
                        modelUsed: streamingMessageMeta?.modelUsed,
                        isStreaming: false
                    }]);
                } else {
                    setMessages(prev => [...prev, { 
                        role: 'system', 
                        content: 'An error occurred with the streaming connection.' 
                    }]);
                }
                
                setStreamingContent('');
                setStreamingMessageMeta(null);
                setIsWaitingForResponse(false);
                setIsSubmitting(false);

                refreshUserProfile().catch(err => {
                    console.error('Failed to refresh profile after message:', err);
                });

            }
        };

        try {
            socketRef.current = await apiChat.createStreamingSocket(
                { 
                    prompt, 
                    chosenModel, 
                    conversationId: convId, 
                    files: uploadedFiles,
                    enableSearch: webSearchEnabled,
                    routingDetails: routingDetails
                },
                callbacks
            );
            console.log('ChatSessionContext: WebSocket created successfully');
        } catch (error) {
            console.error(`ChatSessionContext: Failed to create WebSocket for user ${userId}:`, error);
            setAuthError(error.message);
            setIsWaitingForResponse(false);
            setStreamingContent('');
            setStreamingMessageMeta(null);
        }
    };

    // --- Poll for Routing Result ---
    const pollForResult = (taskId, originalPrompt, uploadedFiles = [], webSearchEnabled = false) => {
        let attempts = 0;
        setIsRouting(true);
        console.log(`ChatSessionContext: Starting polling for task ${taskId}`);

        const executePoll = async () => {
            // Show warming up message after 10 attempts
            if (attempts === 10) {
                setMessages(prev => [...prev, { 
                    role: 'system', 
                    content: 'Still processing... First run takes a bit longer to warm up the system. Thanks for your patience!'
                }]);
            }
            if (attempts >= MAX_POLLING_ATTEMPTS) {
                setMessages(prev => [...prev, { 
                    role: 'system', 
                    content: 'Request timed out. Please try again - the system should respond faster on the next attempt.' 
                }]);
                setIsWaitingForResponse(false);
                setIsRouting(false);
                setIsSubmitting(false);
                console.log('ChatSessionContext: Polling timed out');
                return;
            }

            try {
                const result = await apiChat.fetchRoutingResult(taskId);
                
                if (result.status === 'error') {
                    setMessages(prev => [...prev, {
                        role: 'system',
                        content: 'Routing failed. Please try again.'
                    }]);
                    setIsWaitingForResponse(false);
                    setIsRouting(false);
                    setIsSubmitting(false);
                } else if (result.status !== 'pending') {
                    const routing = result.routing || {};
                    const model = routing.model || 'an appropriate model';

                    const benchmark = routing.benchmark || 'general';
                    const formattedBenchmark = benchmark.charAt(0).toUpperCase() + benchmark.slice(1).replace(/_/g, ' ');

                    // Build and display the system message immediately
                    let systemMessage = `Routing complete!\n`;
                    systemMessage += `Best model is: ${model}\n`;
                    systemMessage += `Optimized for: ${formattedBenchmark} benchmark`;

                    setMessages(prev => [...prev, {
                        role: 'system',
                        content: systemMessage
                    }]);

                    setIsRouting(false);
                    console.log(`ChatSessionContext: Routing complete - Model: ${model}`);

                    // Get conversation ID from result
                    const convId = result.conversationId;

                    streamFinalAnswer(originalPrompt, model, convId, uploadedFiles, webSearchEnabled, routing);
                } else {
                    attempts++;
                    console.log(`ChatSessionContext: Polling attempt ${attempts}/${MAX_POLLING_ATTEMPTS}`);
                    setTimeout(executePoll, POLLING_INTERVAL_MS);
                }

            } catch (error) {
                console.error(`ChatSessionContext: Polling error for user ${userId}:`, error);
                
                // Handle authentication errors
                if (error.message?.includes('Authentication') || 
                    error.message?.includes('Access denied')) {
                    setAuthError(error.message);
                    setIsSessionValid(false);
                }
                
                setMessages(prev => [...prev, { 
                    role: 'system', 
                    content: 'Error fetching routing result. Please try again.' 
                }]);
                setIsWaitingForResponse(false);
                setIsRouting(false);
                setIsSubmitting(false);
            }
        };

        executePoll();
    };

    // --- Start New Chat ---
    const startNewChat = () => {
        console.log(`ChatSessionContext: User ${userId} starting new chat`);

        closeWebSocket();
        closeRoutingSocket();
        saveCurrentChat();
        
        setMessages([]);
        setConversationId(null);
        setIsWaitingForResponse(false);
        setIsRouting(false);
        setInputValue('');
        setViewMode('chat');
        setStreamingContent('');
        setStreamingMessageMeta(null);
    };

    // --- Load Conversation ---
    const loadConversationInChat = async (convId) => {
        if (isWaitingForResponse) {
            console.log('ChatSessionContext: Cannot load conversation while waiting for response');
            return;
        }

        console.log(`ChatSessionContext: Loading conversation ${convId}`);

        closeWebSocket();
        closeRoutingSocket();
        saveCurrentChat();

        setStreamingContent('');
        setStreamingMessageMeta(null);

        try {
            // Show loading state
            setMessages([{ 
                role: 'system', 
                content: 'Loading conversation...' 
            }]);
            
            // Load conversation data from ConversationsContext
            const { conversationId: loadedConvId, messages: loadedMessages } = await loadConversationData(convId);
            
            setMessages(loadedMessages);
            setConversationId(loadedConvId);
            setViewMode('chat');
            
            console.log(`ChatSessionContext: Successfully loaded conversation ${convId}`);
            
        } catch (error) {
            console.error(`ChatSessionContext: Failed to load conversation ${convId}:`, error);
            
            setMessages([{ 
                role: 'system', 
                content: `Error: ${error.message}` 
            }]);
            
            // Clear conversation ID on error
            setConversationId(null);
        }
    };

    // --- Handle Routing Result (shared by push and polling paths) ---
    const _handleRoutingResult = useCallback((data, originalPrompt, uploadedFiles, webSearchEnabled) => {
        const routing = data.routing || {};
        const model = routing.model || 'an appropriate model';
        const benchmark = routing.benchmark || 'general';
        const formattedBenchmark = benchmark.charAt(0).toUpperCase() + benchmark.slice(1).replace(/_/g, ' ');

        setMessages(prev => [...prev, {
            role: 'system',
            content: `Routing complete!\nBest model is: ${model}\nOptimized for: ${formattedBenchmark} benchmark`
        }]);
        setIsRouting(false);
        console.log(`ChatSessionContext: Routing complete via push - Model: ${model}`);

        streamFinalAnswer(originalPrompt, model, data.conversationId, uploadedFiles, webSearchEnabled, routing);
    }, [streamFinalAnswer]);

    // --- Submit Message (without file handling - that's in FileUploadContext) ---
    const submitMessage = async (prompt, uploadedFiles = [], webSearchEnabled = false) => {
        setIsSubmitting(true);
        console.log(`ChatSessionContext: Submitting message for user ${userId}`);
        
        // SECURITY: Don't include userId in conversation ID
        const newConvId = conversationId || crypto.randomUUID();
        
        if (!conversationId) {
            setConversationId(newConvId);
            console.log(`ChatSessionContext: Created new conversation ID: ${newConvId}`);
        }

        const userMessage = {
            role: 'user',
            content: prompt,
            files: uploadedFiles.map(f => ({ 
                name: f.name, 
                size: f.size,
                s3_key: f.s3Key
            })),
            timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, userMessage]);
        setIsWaitingForResponse(true);
        setInputValue('');
        setViewMode('chat');

        if (selectedModel === "Route AI") {
            setIsRouting(true);

            // Mutable flags — JS is single-threaded, no race condition
            let submittedTaskId = null;
            let pendingRoutingResult = null;  // push may arrive before submitPrompt resolves
            let routingHandled = false;       // prevents double-execution of completion/fallback

            const onRoutingComplete = (data) => {
                if (routingHandled) return;
                routingHandled = true;
                _handleRoutingResult(data, prompt, uploadedFiles, webSearchEnabled);
            };

            const onFallbackToPolling = (reason) => {
                if (routingHandled) return;
                routingHandled = true;
                // No preemptive "taking longer" message here — pollForResult already shows
                // "Still processing..." after 10 attempts if the worker is genuinely slow.
                if (submittedTaskId) {
                    pollForResult(submittedTaskId, prompt, uploadedFiles, webSearchEnabled);
                } else {
                    setMessages(prev => [...prev, { role: 'system', content: 'Sorry, something went wrong. Please try again.' }]);
                    setIsWaitingForResponse(false);
                    setIsRouting(false);
                    setIsSubmitting(false);
                }
            };

            try {
                routingSocketRef.current = await apiChat.createRoutingSocket({

                    onConnectionId: async (connectionId) => {
                        try {
                            const intakeData = await apiChat.submitPrompt(
                                prompt, newConvId, uploadedFiles, connectionId
                            );
                            if (intakeData.conversationId) setConversationId(intakeData.conversationId);
                            submittedTaskId = intakeData.task_id;
                            // Handle the rare case where push arrived before submitPrompt returned
                            if (pendingRoutingResult) {
                                onRoutingComplete(pendingRoutingResult);
                                pendingRoutingResult = null;
                            }
                        } catch (intakeError) {
                            console.error('ChatSessionContext: Intake failed:', intakeError);
                            closeRoutingSocket();
                            if (intakeError.message?.includes('Authentication')) {
                                setAuthError(intakeError.message);
                                setIsSessionValid(false);
                            }
                            setMessages(prev => [...prev, { role: 'system', content: intakeError.message || 'Sorry, something went wrong submitting your request.' }]);
                            setIsWaitingForResponse(false);
                            setIsRouting(false);
                            setIsSubmitting(false);
                        }
                    },

                    onRoutingResult: (data) => {
                        if (!submittedTaskId) {
                            pendingRoutingResult = data;  // buffer until onConnectionId finishes
                        } else {
                            onRoutingComplete(data);
                        }
                    },

                    onTimeout: () => {
                        onFallbackToPolling('timeout');
                    },

                    onError: () => {
                        onFallbackToPolling('error');
                    }
                });

            } catch (wsCreateError) {
                // createRoutingSocket threw (e.g. token fetch failed) — polling-only fallback.
                // Guard: onError may have already run via onerror before the throw.
                if (routingHandled) return;
                console.error('ChatSessionContext: Could not open routing WebSocket, using polling fallback.');
                try {
                    const intakeData = await apiChat.submitPrompt(prompt, newConvId, uploadedFiles);
                    if (intakeData.conversationId) setConversationId(intakeData.conversationId);
                    if (intakeData.task_id) {
                        pollForResult(intakeData.task_id, prompt, uploadedFiles, webSearchEnabled);
                    } else {
                        throw new Error("No task_id received from intake API.");
                    }
                } catch (error) {
                    if (error.message?.includes('Authentication')) { setAuthError(error.message); setIsSessionValid(false); }
                    setMessages(prev => [...prev, { role: 'system', content: error.message || 'Sorry, something went wrong.' }]);
                    setIsWaitingForResponse(false);
                    setIsRouting(false);
                    setIsSubmitting(false);
                }
            }
        } else {
            streamFinalAnswer(prompt, selectedModel, newConvId, uploadedFiles, webSearchEnabled);
        }
    };

    // --- Stop Generating ---
    const stopGenerating = () => {
        console.log('ChatSessionContext: Stopping generation');

        closeWebSocket();
        closeRoutingSocket();
        setIsRouting(false);
        setIsWaitingForResponse(false);
        
        if (streamingContent) {
            const stoppedMessage = {
                role: 'assistant',
                content: streamingContent + '\n\n*[Generation stopped by user]*',
                modelUsed: streamingMessageMeta?.modelUsed || selectedModel,
                isStreaming: false
            };
            setMessages(prev => [...prev, stoppedMessage]);
        }
        
        setStreamingContent('');
        setStreamingMessageMeta(null);
    };

    const value = {
        // State
        messages,
        inputValue,
        setInputValue,
        isWaitingForResponse,
        isRouting,
        isSubmitting,
        conversationId,
        selectedModel,
        setSelectedModel,
        streamingContent,
        streamingMessageMeta,
        viewMode,
        setViewMode,
        
        // Actions
        startNewChat,
        loadConversationInChat,
        stopGenerating,
        submitMessage,
        saveCurrentChat
    };

    return (
        <ChatSessionContext.Provider value={value}>
            {children}
        </ChatSessionContext.Provider>
    );
};

export const useChatSession = () => {
    const context = useContext(ChatSessionContext);
    if (context === undefined) {
        throw new Error('useChatSession must be used within a ChatSessionProvider');
    }
    return context;
};